import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from './AppShell.tsx';
import { useI18n, useTranslate } from '../i18n/index.tsx';
import { Centered } from '../components/Centered.tsx';
import { ProtectedRoute } from '../components/ProtectedRoute.tsx';
import { AlertsPage } from '../features/dashboard/AlertsPage.tsx';
import { CoachDetailPage } from '../features/coaches/CoachDetailPage.tsx';
import { CoachesPage } from '../features/coaches/CoachesPage.tsx';
import { PeoplePage } from '../features/staff/PeoplePage.tsx';
import { DashboardPage } from '../features/dashboard/DashboardPage.tsx';
import { MagicLinkPage } from '../features/auth/MagicLinkPage.tsx';
import { NotFoundPage } from '../components/NotFoundPage.tsx';
import { SignInPage } from '../features/auth/SignInPage.tsx';
import { ParticipantDetailPage } from '../features/participants/ParticipantDetailPage.tsx';
import { IntroPage } from '../features/intro/IntroPage.tsx';
import { PrivacyPage } from '../features/privacy/PrivacyPage.tsx';
import { ParticipantsPage } from '../features/participants/ParticipantsPage.tsx';
import { RaterDetailPage } from '../features/raters/RaterDetailPage.tsx';
import { RatersPage } from '../features/raters/RatersPage.tsx';
import { ReportPage } from '../features/report/ReportPage.tsx';
import { SurveyPage } from '../features/survey/SurveyPage.tsx';
import { useAuthStore } from '../stores/auth.store.ts';

/**
 * Routes are Spanish-facing because that is the language the client works in;
 * the code around them stays English.
 */
/**
 * Where signing in lands you.
 *
 * Everybody used to land on the dashboard, which a leader cannot read — so the
 * first thing they saw after signing in was "you do not have access to this
 * section", about their own product.
 */
/**
 * Adopt the language stored on the person, once the session arrives.
 *
 * The first paint uses whatever they last chose in this browser; the server is
 * what makes that choice follow them to another machine.
 */
const useLocaleFromSession = () => {
  const user = useAuthStore((state) => state.user);
  const { locale, setLocale } = useI18n();

  useEffect(() => {
    if (user?.locale && user.locale !== locale) setLocale(user.locale);
    // Only when the session changes: a later manual switch must not be undone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.locale]);
};

/**
 * Where a session belongs, decided by what it may read.
 *
 * The session check has to have resolved first. `can` answers false for
 * everything while the user is still null, so deciding early sent every
 * arrival to the dashboard — and a leader who opens the site at its root, or
 * follows a sign-in link, has no dashboard: they were told they had no access
 * to the product they had just been handed. The status subscription is what
 * makes this run again once the check lands.
 */
const Landing = () => {
  const status = useAuthStore((state) => state.status);
  const can = useAuthStore((state) => state.can);
  const t = useTranslate();

  if (status === 'loading') return <Centered>{t('common.loading')}</Centered>;
  if (status === 'anonymous') return <Navigate to="/acceso" replace />;
  if (can('progress:read')) return <Navigate to="/panel" replace />;
  if (can('report:read:own')) return <Navigate to="/mi-reporte" replace />;
  // A session holding neither permission is a rater, and a rater has no page
  // here: their questionnaire is resolved from the link they were sent, not
  // from a route. They land on the dashboard's refusal, which is wrong but is
  // at least written in their language — sending them to /encuesta without a
  // survey on the session trades it for a raw API error.
  return <Navigate to="/panel" replace />;
};

export const App = () => {
  useLocaleFromSession();

  const restore = useAuthStore((state) => state.restore);

  useEffect(() => {
    void restore();
  }, [restore]);

  return (
    <Routes>
      <Route path="/acceso" element={<SignInPage />} />
      {/* Both magic-link shapes land on the same redeem screen. */}
      <Route path="/acceso/:token" element={<MagicLinkPage />} />
      <Route path="/encuesta/:token" element={<MagicLinkPage />} />

      <Route path="/encuesta" element={<SurveyPage />} />

      {/* Public, and outside every guard: a rater deciding whether to be candid
          has no session, and somebody who followed the link from the survey
          must not hit a sign-in wall to read what happens to their answers. */}
      <Route path="/privacidad" element={<PrivacyPage />} />

      {/* The introduction video. Public, and meant to be pasted into a message:
          whoever opens it has no session and may not have been invited yet. */}
      <Route path="/intro" element={<IntroPage />} />

      {/* The survey is deliberately outside the shell: a rater has no
          navigation, only the questionnaire they were sent. */}
      <Route
        path="/panel"
        element={
          <ProtectedRoute permission="progress:read">
            <AppShell>
              <DashboardPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/participantes"
        element={
          <ProtectedRoute permission="progress:read">
            <AppShell>
              <ParticipantsPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/participantes/:participantId"
        element={
          <ProtectedRoute permission="progress:read">
            <AppShell>
              <ParticipantDetailPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/alertas"
        element={
          <ProtectedRoute permission="progress:read">
            <AppShell>
              <AlertsPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/equipo-po"
        element={
          <ProtectedRoute permission="staff:manage">
            <AppShell>
              <PeoplePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/coaches"
        element={
          <ProtectedRoute permission="coach:read">
            <AppShell>
              <CoachesPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/coaches/:personId"
        element={
          <ProtectedRoute permission="coach:read">
            <AppShell>
              <CoachDetailPage />
            </AppShell>
          </ProtectedRoute>
        }
      />


      <Route
        path="/evaluadores"
        element={
          <ProtectedRoute permission="rater:read">
            <AppShell>
              <RatersPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/evaluadores/:personId"
        element={
          <ProtectedRoute permission="rater:read">
            <AppShell>
              <RaterDetailPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Who may read a report is decided by the API from the report itself —
          coach, the leader once published, or an admin — so the route only
          requires a session. */}
      <Route
        path="/participantes/:participantId/reporte"
        element={
          <ProtectedRoute>
            <AppShell>
              <ReportPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* A leader's whole product: one page, once a coach publishes it. */}
      <Route
        path="/mi-reporte"
        element={
          <ProtectedRoute permission="report:read:own">
            <AppShell>
              <ReportPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route path="/" element={<Landing />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};
